import { useCallback, useMemo } from "react";

import Identified from "@mapmycustomers/shared/types/base/Identified";
import Named from "@mapmycustomers/shared/types/base/Named";
import { primaryEntityComparator } from "@mapmycustomers/shared/util/comparator";

const useEntitySorter = <T extends Identified & Named>(
  entities: T[],
  createdEntities: T[],
  isFilterApplied: boolean,
  selectedEntities?: T[],
  associatedEntities?: T[],
  primaryEntityId?: T["id"]
) => {
  const selectedEntitiesIds = useMemo(
    () => (selectedEntities ?? []).map(({ id }) => id),
    [selectedEntities]
  );

  const associatedEntitiesIds = useMemo(
    () => (associatedEntities ?? []).map(({ id }) => id),
    [associatedEntities]
  );

  const createdEntitiesIds = useMemo(
    () => (createdEntities ?? []).map(({ id }) => id),
    [createdEntities]
  );

  const compareByCreatedEntities = useCallback(
    (a: T, b: T) => {
      if (createdEntitiesIds.includes(a.id)) {
        return createdEntitiesIds.includes(b.id) ? 0 : -1;
      }
      if (createdEntitiesIds.includes(b.id)) {
        return 1;
      }
      return 0;
    },
    [createdEntitiesIds]
  );

  const compareByAssociatedEntities = useCallback(
    (a: T, b: T) => {
      if (associatedEntitiesIds.length) {
        if (associatedEntitiesIds.includes(a.id)) {
          return associatedEntitiesIds.includes(b.id) ? 0 : -1;
        }
        if (associatedEntitiesIds.includes(b.id)) {
          return 1;
        }
      }
      return 0;
    },
    [associatedEntitiesIds]
  );

  const compareBySelectedEntities = useCallback(
    (a: T, b: T) => {
      if (selectedEntitiesIds.length) {
        if (selectedEntitiesIds.includes(a.id)) {
          return selectedEntitiesIds.includes(b.id) ? 0 : -1;
        }
        if (selectedEntitiesIds.includes(b.id)) {
          return 1;
        }
      }
      return 0;
    },
    [selectedEntitiesIds]
  );

  const compareByName = useCallback((a: T, b: T) => (a.name ?? "").localeCompare(b.name ?? ""), []);

  return useMemo(() => {
    // take all new entities and existing entities (avoid duplicates though) and sort them
    // however if filter is applied, do not include createdEntities (and do not sort, since entities are
    // assumed to be sorted by name already)
    return isFilterApplied
      ? entities
      : [
          // Only the list fetch carries pinLegends, so a locally held copy still has the record's
          // stored color. Wherever the fetched copy exists it wins; ordering is by id, so unaffected.
          ...(selectedEntities ?? []).filter(
            ({ id }) => !entities.some((entity) => entity.id === id)
          ),
          ...createdEntities.filter(
            ({ id }) =>
              !selectedEntitiesIds?.includes(id) && !entities.some((entity) => entity.id === id)
          ),
          ...(associatedEntities ?? []).filter(
            ({ id }) =>
              !selectedEntitiesIds?.includes(id) && !entities.some((entity) => entity.id === id)
          ),
          ...entities,
        ].sort((a, b) => {
          return (
            primaryEntityComparator(primaryEntityId)(a, b) ||
            compareBySelectedEntities(a, b) ||
            compareByAssociatedEntities(a, b) ||
            compareByCreatedEntities(a, b) ||
            compareByName(a, b)
          );
        });
  }, [
    isFilterApplied,
    entities,
    selectedEntities,
    associatedEntities,
    createdEntities,
    selectedEntitiesIds,
    primaryEntityId,
    compareBySelectedEntities,
    compareByAssociatedEntities,
    compareByCreatedEntities,
    compareByName,
  ]);
};

export default useEntitySorter;
